/**
 * מספר הזמנה לתצוגת סוכן/לקוח בטיולים מאורגנים — ללא UUID גולמי.
 * SSOT: שדות HR על שורת ההזמנה + הצעה + אותם כללי נירמול כמו כרטיסי תוצאות / לקוח.
 */
import { formatHrBookingRef } from "./bookingRefDisplay";
import {
  formatHrLabel,
  isLikelyOpaqueLdsDealToken,
  resolveCustomerFacingOfferNumber,
} from "./resolveCustomerFacingOfferNumber";
import { resolveOrganizedTourSearchDisplayNumber } from "./resolveOrganizedTourSearchDisplayNumber";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** @param {unknown} v */
function trimStr(v) {
  if (v == null) return "";
  if (typeof v === "number" && Number.isFinite(v)) return String(v).trim();
  if (typeof v === "string") return v.trim();
  return "";
}

/** @param {unknown} s */
export function isTechnicalOrderUuid(s) {
  return UUID_RE.test(trimStr(s));
}

/** @param {string} raw */
function fnv1aHash32(raw) {
  let h = 2166136261;
  for (let i = 0; i < raw.length; i++) {
    h ^= raw.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Fallback יחיד כשאין HR מהמערכת — קצר, קריא, לא UUID מלא.
 * @param {unknown} technicalOrderId
 * @returns {string}
 */
export function buildOrganizedTourOrderDisplayFallback(technicalOrderId) {
  const raw = trimStr(technicalOrderId);
  if (!raw) return "HR-00000";
  const n = fnv1aHash32(raw) % 60466176;
  let s = n.toString(36).toUpperCase();
  while (s.length < 5) s = `0${s}`;
  return `HR-${s.slice(-5)}`;
}

/** @param {unknown} s */
function isForbiddenRawOrderToken(s) {
  const t = trimStr(s);
  if (!t) return true;
  if (isTechnicalOrderUuid(t)) return true;
  if (isLikelyOpaqueLdsDealToken(t)) return true;
  if (t.length > 64) return true;
  return false;
}

/**
 * @param {unknown} raw
 * @returns {string|null}
 */
function normalizeDisplayableOrderToken(raw) {
  const t = trimStr(raw);
  if (!t || isForbiddenRawOrderToken(t)) return null;

  const compact = t.replace(/\s/g, "");
  if (/^HR-[1-9]\d{7}$/i.test(compact)) {
    return compact.replace(/^hr-/i, "HR-");
  }

  const digits = t.replace(/\D/g, "");
  if (digits.length === 8 && /^[1-9]\d{7}$/.test(digits)) {
    const hr8 = formatHrBookingRef(digits);
    if (hr8) return hr8;
  }

  const fl = formatHrLabel(t);
  if (fl) return fl;

  const br = formatHrBookingRef(t);
  if (br) return br;

  if (t.length >= 3 && t.length <= 24 && /^[A-Za-z0-9\-_/]+$/.test(t)) {
    return t;
  }

  return null;
}

function pickOurPriceLayer(order) {
  if (!order || typeof order !== "object") return null;
  return order.ourPrice ?? order.ourprice ?? null;
}

/**
 * @param {{
 *   order?: object|null,
 *   technicalOrderId?: string|number|null,
 *   ourPrice?: object|null,
 *   selectedResult?: object|null,
 * }} input
 * @returns {{ displayNumber: string, source: string, technicalOrderId: string|null }}
 */
export function resolveOrganizedTourDisplayOrderNumber(input) {
  const order = input.order && typeof input.order === "object" ? input.order : null;
  const tech =
    trimStr(input.technicalOrderId) ||
    trimStr(order?.id) ||
    trimStr(order?.ourLdsId) ||
    trimStr(order?.our_lds_id) ||
    null;

  const op = input.ourPrice && typeof input.ourPrice === "object" ? input.ourPrice : pickOurPriceLayer(order);

  const tryReturn = (raw, source) => {
    const n = normalizeDisplayableOrderToken(raw);
    if (!n) return null;
    const out = { displayNumber: n, source, technicalOrderId: tech };
    if (typeof console !== "undefined" && console.info) {
      console.info("[ORG_TOUR_DISPLAY_ORDER_ID_RESOLVE]", out);
    }
    return out;
  };

  const hrFields = [
    [order?.hr_order_number, "order.hr_order_number"],
    [order?.hrOrderNumber, "order.hrOrderNumber"],
    [order?.display_order_number, "order.display_order_number"],
    [order?.displayOrderNumber, "order.displayOrderNumber"],
    [order?.displayOrderId, "order.displayOrderId"],
    [order?.display_order_id, "order.display_order_id"],
  ];

  for (const [raw, src] of hrFields) {
    const hit = tryReturn(raw, src);
    if (hit) return hit;
  }

  const offerTries = [];
  if (op && typeof op === "object") {
    offerTries.push([op.offerNumber, "ourPrice.offerNumber"]);
    offerTries.push([op.offer_number, "ourPrice.offer_number"]);
    const cqd = op.customerQuoteDisplay ?? op.customer_quote_display;
    if (cqd && typeof cqd === "object") {
      offerTries.push([cqd.offerNumber, "ourPrice.customerQuoteDisplay.offerNumber"]);
      offerTries.push([cqd.offer_number, "ourPrice.customerQuoteDisplay.offer_number"]);
    }
  }
  if (order) {
    offerTries.push([order.offerNumber, "order.offerNumber"]);
    offerTries.push([order.offer_number, "order.offer_number"]);
  }

  for (const [raw, src] of offerTries) {
    const hit = tryReturn(raw, src);
    if (hit) return hit;
  }

  const facing = resolveCustomerFacingOfferNumber(order);
  if (facing.show && facing.value && !isForbiddenRawOrderToken(facing.value)) {
    const hit = tryReturn(facing.value, "resolveCustomerFacingOfferNumber");
    if (hit) return hit;
  }

  const sr = input.selectedResult && typeof input.selectedResult === "object" ? input.selectedResult : null;
  if (sr) {
    const card = resolveOrganizedTourSearchDisplayNumber(sr);
    if (card.show && card.value) {
      const hit = tryReturn(card.value, `selectedResult.${card.source || "resolveOrganizedTourSearchDisplayNumber"}`);
      if (hit) return hit;
    }
  }

  const fb = buildOrganizedTourOrderDisplayFallback(tech);
  const out = { displayNumber: fb, source: "fallback.hr_compact", technicalOrderId: tech };
  if (typeof console !== "undefined" && console.info) {
    console.info("[ORG_TOUR_DISPLAY_ORDER_ID_RESOLVE]", out);
  }
  return out;
}
